import React from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslations } from '../context/TranslationContext';

interface HeaderProps {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  closeMenu: () => void;
  hideNavLinks?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isMenuOpen, toggleMenu, closeMenu, hideNavLinks }) => {
  const { t } = useTranslations();
  const [productsOpen, setProductsOpen] = React.useState(false);
  const products = [
    ['LegisGraph', t('legisgraph_card_title'), '/legisgraph'],
    ['LegisChat', t('legischat_card_title'), '/legischat'],
    ['Doc2Graph', t('doc2graph_card_title'), '/doc2graph'],
  ];

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && closeMenu();
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isMenuOpen, closeMenu]);

  return (
    <header className="fixed left-0 top-0 z-50 w-full border-b border-white/10 bg-void-blue/95 py-4 backdrop-blur-xl">
      <div className="container relative mx-auto flex items-center justify-between px-6 md:px-12">
        <Link to="/" onClick={closeMenu} className="shrink-0">
          <img src="/images/logoLegis.png" alt="LegisRatio" className="h-10 w-auto brightness-0 invert" />
        </Link>

        {!hideNavLinks && (
          <nav className="absolute left-1/2 hidden -translate-x-1/2 xl:block" aria-label={t('main_navigation')}>
            <ul className="flex items-center gap-8 font-bold text-white/80">
              <li><Link to="/" className="transition-colors hover:text-mystic-violet">{t('home')}</Link></li>
              <li className="relative" onMouseEnter={() => setProductsOpen(true)} onMouseLeave={() => setProductsOpen(false)}>
                <button onClick={() => setProductsOpen(value => !value)} aria-expanded={productsOpen} className="flex items-center gap-1 transition-colors hover:text-mystic-violet">
                  {t('products')}<ChevronDown size={16} className={productsOpen ? 'rotate-180' : ''}/>
                </button>
                {productsOpen && (
                  <div className="absolute left-0 top-full w-72 pt-3">
                    <div className="rounded-xl border border-white/10 bg-deep-space p-2 shadow-2xl">
                      {products.map(([name,description,path]) => (
                        <Link key={name} to={path} onClick={() => setProductsOpen(false)} className="block rounded-lg px-4 py-3 hover:bg-white/10">
                          <span className="block text-white">{name}</span><span className="mt-1 block text-sm font-normal leading-5 text-white">{description}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>
              <li><Link to="/azienda" className="transition-colors hover:text-mystic-violet">{t('azienda')}</Link></li>
              <li><Link to="/faq" className="transition-colors hover:text-mystic-violet">{t('domande_frequenti')}</Link></li>
            </ul>
          </nav>
        )}

        <div className="hidden items-center gap-6 xl:flex">
          <LanguageSwitcher />
          <a href="mailto:info@legisratio.it" className="rounded-full bg-gradient-to-r from-mystic-violet to-nebula-pink px-6 py-2.5 font-bold text-white transition-shadow hover:shadow-glow-violet">{t('contact_us')}</a>
          <a href="https://apps.legisratio.com/" className="rounded-full border border-white/30 px-6 py-2.5 font-bold text-white transition-colors hover:border-mystic-violet hover:text-mystic-violet">{t('sign_in')}</a>
        </div>

        <button id="mobile-menu-toggle" onClick={toggleMenu} aria-expanded={isMenuOpen} aria-controls="mobile-menu" aria-label={isMenuOpen ? t('close_menu') : t('open_menu')} className="rounded-lg p-2 xl:hidden">
          {isMenuOpen ? <X/> : <Menu/>}
        </button>
      </div>

      {isMenuOpen && !hideNavLinks && (
        <div id="mobile-menu" className="fixed inset-0 top-[73px] h-[calc(100vh-73px)] overflow-y-auto bg-void-blue px-6 py-8 xl:hidden">
          <nav aria-label={t('mobile_navigation')}>
            <Link to="/" onClick={closeMenu} className="block border-b border-white/10 py-4 font-display text-2xl font-bold">{t('home')}</Link>
            <p className="mt-7 text-sm font-bold uppercase leading-5 tracking-widest text-white/70">{t('products')}</p>
            <div className="mt-3 space-y-3">
              {products.map(([name,description,path]) => <Link key={name} to={path} onClick={closeMenu} className="block rounded-xl border border-white/10 bg-deep-space p-4"><span className="block text-lg font-bold">{name}</span><span className="mt-1 block text-sm text-white">{description}</span></Link>)}
            </div>
            <Link to="/azienda" onClick={closeMenu} className="mt-5 block border-b border-white/10 py-4 font-display text-2xl font-bold">{t('azienda')}</Link>
            <Link to="/faq" onClick={closeMenu} className="block border-b border-white/10 py-4 font-display text-2xl font-bold">{t('domande_frequenti')}</Link>
            <div className="mt-8 flex items-center justify-between"><LanguageSwitcher/><div className="flex items-center gap-3"><a href="https://apps.legisratio.com/" className="rounded-full border border-white/30 px-5 py-3 font-bold">{t('sign_in')}</a><a href="mailto:info@legisratio.it" className="rounded-full bg-gradient-ai px-5 py-3 font-bold">{t('contact_us')}</a></div></div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
